"use client"

import { ActionIcon, Box, Tooltip } from "@mantine/core"
import { IconChevronCompactLeft, IconChevronCompactRight } from "@tabler/icons-react"
import { use$, useSelector } from "@legendapp/state/react"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { checkChromeExtensionInstalled, getChromeExtensionSidePanelState, sendChromeExtensionMessage } from "@/lib/chromeExtensionMessaging"
import { showNotification } from "@/lib/notifications"
import { chatStore$ } from "@/lib/ContactsMap"
import { syncStore$ } from "@/stores/sync.v2.store"
import classes from "@/components/webphone/ChromeExtensionSidePanelToggle.module.css"
import { getEmbeddedSidePanelState, getEmbeddedWebphoneStatus } from "@/hooks/embeddedEvents.helpers"

const SIDE_PANEL_OPEN_STORAGE_KEY = "chromeExtensionSidePanelOpen"
const SUPPORTED_LAYOUT_EVENT_SOURCES = new Set(["ringio-webphone", "ringio-chrome-extension"])

function getInitialSidePanelOpen(pathname: string): boolean {
  if (typeof window === "undefined") return pathname.startsWith("/messenger")

  const savedValue = window.localStorage.getItem(SIDE_PANEL_OPEN_STORAGE_KEY)
  if (savedValue === "true") return true
  if (savedValue === "false") return false

  return pathname.startsWith("/messenger")
}

function isOpenSidePanelState(state: string | null) {
  return state === "open" || state === "opened" || state === "ready"
}

export function ChromeExtensionSidePanelToggle() {
  const pathname = usePathname()
  const extensionId = use$(syncStore$.cluster.chromeExtensionWebstoreId.get())
  const [extensionInstalled, setExtensionInstalled] = useState(false)
  const [isCheckingExtension, setIsCheckingExtension] = useState(true)
  const [sidePanelOpen, setSidePanelOpen] = useState(() => getInitialSidePanelOpen(pathname))
  const hasLiveCall = useSelector(() => chatStore$.liveCalls.get().length > 0)
  const syncInFlightRef = useRef(false)

  const syncExtensionStatus = useCallback(
    async (options?: { silent?: boolean }) => {
      const { silent = true } = options ?? {}

      if (syncInFlightRef.current) return
      syncInFlightRef.current = true

      if (!silent) {
        setIsCheckingExtension(true)
      }

      try {
        const isInstalled = await checkChromeExtensionInstalled(extensionId)
        setExtensionInstalled(isInstalled)

        if (isInstalled) {
          const sidePanelState = await getChromeExtensionSidePanelState(extensionId)

          if (sidePanelState) {
            setSidePanelOpen(isOpenSidePanelState(sidePanelState))
          }
        } else {
          setSidePanelOpen(false)
        }
      } finally {
        syncInFlightRef.current = false
        if (!silent) {
          setIsCheckingExtension(false)
        }
      }
    },
    [extensionId]
  )

  useEffect(() => {
    void syncExtensionStatus({ silent: false })
  }, [syncExtensionStatus])

  useEffect(() => {
    const handleWindowFocus = () => {
      void syncExtensionStatus()
    }
    const handlePageShow = () => {
      void syncExtensionStatus()
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void syncExtensionStatus()
      }
    }
    const handleMessage = (event: MessageEvent) => {
      const maybeWebphoneLayoutEvent = event.data as { from?: string; type?: string; data?: unknown } | null
      if (SUPPORTED_LAYOUT_EVENT_SOURCES.has(maybeWebphoneLayoutEvent?.from ?? "") && maybeWebphoneLayoutEvent?.type === "layout") {
        const sidePanelState = getEmbeddedSidePanelState(maybeWebphoneLayoutEvent.data)
        if (sidePanelState) {
          setExtensionInstalled(true)
          setSidePanelOpen(isOpenSidePanelState(sidePanelState))
          return
        }

        const webphoneStatus = getEmbeddedWebphoneStatus(maybeWebphoneLayoutEvent.data)
        if (webphoneStatus === "expanded") {
          setSidePanelOpen(true)
        } else if (webphoneStatus === "collapsed") {
          setSidePanelOpen(false)
        } else {
          void syncExtensionStatus()
        }
        return
      }

      if (event.origin.startsWith("chrome-extension://")) {
        void syncExtensionStatus()
      }
    }

    window.addEventListener("focus", handleWindowFocus)
    window.addEventListener("pageshow", handlePageShow)
    window.addEventListener("message", handleMessage)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.removeEventListener("focus", handleWindowFocus)
      window.removeEventListener("pageshow", handlePageShow)
      window.removeEventListener("message", handleMessage)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [syncExtensionStatus])

  useEffect(() => {
    window.localStorage.setItem(SIDE_PANEL_OPEN_STORAGE_KEY, sidePanelOpen ? "true" : "false")
  }, [sidePanelOpen])

  const toggleLabel = useMemo(() => (sidePanelOpen ? "Close webphone sidebar" : "Open webphone sidebar"), [sidePanelOpen])

  const handleToggle = useCallback(async () => {
    if (sidePanelOpen && hasLiveCall) {
      const shouldClose = window.confirm("You are currently in a call. Closing the webphone sidebar will end this call. Continue?")
      if (!shouldClose) return
    }

    if (!sidePanelOpen) {
      try {
        await sendChromeExtensionMessage(extensionId, "openSidePanel")
        setExtensionInstalled(true)
        setSidePanelOpen(true)
        void syncExtensionStatus()
        return
      } catch {
        void syncExtensionStatus()
        showNotification({
          type: "error",
          title: "Chrome extension unavailable",
          message: "Install or enable the Ring.io Chrome extension to use the webphone sidebar.",
        })
        return
      }
    }

    try {
      await sendChromeExtensionMessage(extensionId, "closeSidePanel")
      setExtensionInstalled(true)
      setSidePanelOpen(false)
      void syncExtensionStatus()
    } catch {
      void syncExtensionStatus()
      showNotification({
        type: "error",
        title: "Chrome extension unavailable",
        message: "Install or enable the Ring.io Chrome extension to use the webphone sidebar.",
      })
    }
  }, [extensionId, hasLiveCall, sidePanelOpen, syncExtensionStatus])

  if (isCheckingExtension || !extensionInstalled) return null

  const Icon = sidePanelOpen ? IconChevronCompactRight : IconChevronCompactLeft

  return (
    <Box className={classes.container}>
      <Tooltip label={toggleLabel} position="left">
        <ActionIcon aria-label={toggleLabel} className={classes.button} variant="subtle" color="gray" onClick={handleToggle}>
          <Icon size={24} />
        </ActionIcon>
      </Tooltip>
    </Box>
  )
}
